import { useState, type KeyboardEvent } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface ChipListInputProps {
  readonly id: string;
  readonly label: string;
  readonly values: readonly string[];
  readonly max: number;
  readonly placeholder: string;
  /** Normaliza/valida a entrada; null = invalida (mostra errorText). */
  readonly normalize: (raw: string) => string | null;
  readonly errorText: string;
  readonly onChange: (values: string[]) => void;
}

type MergeResult = { readonly next: string[] } | { readonly error: string };

/** Junta o rascunho (separado por virgula) aos chips, sem duplicar e com teto. */
function mergeDraft(
  values: readonly string[],
  draft: string,
  props: Pick<ChipListInputProps, "normalize" | "errorText" | "max">,
): MergeResult | null {
  const parts = draft
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  const next = [...values];
  for (const part of parts) {
    const value = props.normalize(part);
    if (value === null) return { error: props.errorText };
    if (!next.some((v) => v.toLowerCase() === value.toLowerCase())) next.push(value);
  }
  return next.length > props.max ? { error: `Limite de ${props.max} itens.` } : { next };
}

function Chips({
  label,
  values,
  onRemove,
}: {
  label: string;
  values: readonly string[];
  onRemove: (v: string) => void;
}): JSX.Element {
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={label}>
      {values.map((value) => (
        <li
          key={value}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-[hsl(var(--surface-raised))] py-0.5 pl-2.5 pr-1 text-xs text-foreground"
        >
          {value}
          <button
            type="button"
            onClick={() => onRemove(value)}
            aria-label={`Remover ${value}`}
            className="rounded-full p-0.5 text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
          >
            <X size={11} />
          </button>
        </li>
      ))}
    </ul>
  );
}

function ChipHeader({
  id,
  label,
  count,
  max,
}: {
  id: string;
  label: string;
  count: number;
  max: number;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </label>
      <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
        {count}/{max}
      </span>
    </div>
  );
}

function ChipEntry({
  id,
  label,
  draft,
  placeholder,
  full,
  invalid,
  onDraft,
  onKeyDown,
  onAdd,
}: {
  id: string;
  label: string;
  draft: string;
  placeholder: string;
  full: boolean;
  invalid: boolean;
  onDraft: (v: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onAdd: () => void;
}): JSX.Element {
  return (
    <div className="flex gap-2">
      <Input
        id={id}
        value={draft}
        placeholder={placeholder}
        disabled={full}
        onChange={(e) => onDraft(e.target.value)}
        onKeyDown={onKeyDown}
        aria-invalid={invalid}
      />
      <Button
        type="button"
        variant="secondary"
        size="icon"
        onClick={onAdd}
        disabled={full}
        aria-label={`Adicionar ${label.toLowerCase()}`}
      >
        <Plus size={15} />
      </Button>
    </div>
  );
}

/** Lista de chips com teto (termos a ignorar, encurtadores). Enter ou virgula adiciona. */
export function ChipListInput(props: ChipListInputProps): JSX.Element {
  const { id, label, values, max, placeholder, onChange } = props;
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const full = values.length >= max;

  const add = (): void => {
    const result = mergeDraft(values, draft, props);
    if (!result) return;
    if ("error" in result) {
      setError(result.error);
      return;
    }
    setError(null);
    setDraft("");
    onChange(result.next);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      add();
    }
  };

  return (
    <div className="space-y-2">
      <ChipHeader id={id} label={label} count={values.length} max={max} />
      <ChipEntry
        id={id}
        label={label}
        draft={draft}
        placeholder={full ? "Limite atingido" : placeholder}
        full={full}
        invalid={error !== null}
        onDraft={setDraft}
        onKeyDown={onKeyDown}
        onAdd={add}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      {values.length > 0 && (
        <Chips
          label={label}
          values={values}
          onRemove={(v) => onChange(values.filter((x) => x !== v))}
        />
      )}
    </div>
  );
}
